import './google-pay-button';
import store from '../../../store/store';
import { CartPaymentMethod, CartPaymentMethodTemplate } from '../cart-payment-method';
import { PaymentDetails, RmsBookstoreAddress } from '../../checkout/shop-checkout.interface';
import { StoreState } from '../../../store/store.interface';
import { connect } from 'pwa-helpers';
import { customElement } from 'lit/decorators.js';
import { html, nothing } from 'lit';
import { isLoadingBraintreeDependencies } from '../utils/braintree.util';
import { loading } from '../../../common/constants/api-constants';
import { updateBillingAddress, updateGooglePayBillingAddress, updatePaymentDetails, updateShippingAddress } from '../../checkout/shop-checkout';

/**
 * Google Pay payment method that loads Google Pay button in the shopping cart page.
 */
@customElement(`mhecomm-cart-google-pay-payment-method`)
export class GooglePayCartPaymentMethod extends connect(store)(CartPaymentMethod) implements CartPaymentMethodTemplate {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mhecomm-google-pay-checkout-info-collected', this.onCollectGooglePayInfo);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mhecomm-google-pay-checkout-info-collected', this.onCollectGooglePayInfo);
  }

  /**
   * Verifies if the dependencies for Google Pay are being loaded.
   */
  override isLoadingDependencies(): boolean {
    return isLoadingBraintreeDependencies();
  }

  /**
   * Verifies if Google Pay is enabled.
   * @param _state
   */
  override isPaymentMethodEnabled(_state: StoreState): boolean {
    return !!_state.shopCheckoutState.clientToken.data?.googlePayEnabled;
  }

  /**
   * Updates billing address fields in the store.
   */
  updateBillingAddress(_address: RmsBookstoreAddress): void {
    store.dispatch(updateBillingAddress(_address));
    store.dispatch(updateGooglePayBillingAddress(_address));
  }

  /**
   * Updates shipping address fields in the store.
   */
  updateShippingAddress(_address: RmsBookstoreAddress): void {
    store.dispatch(updateShippingAddress(_address));
  }

  /**
   * Action to be executed when Google Pay processing is complete.
   */
  terminatePaymentMethodProcessing(): void {
    return;
  }

  renderComp() {
    return html`
      <div class="${this.isLoading ? "d-none" : ""}">
        ${this.showPaymentButton ?
          html`
            <mhecomm-google-pay-button
              amount="${this.totalAmount}"
              buttonColor="black"
              buttonType="checkout"
              buttonSize="large"
              clientToken="${this.clientToken}"
              currency="${this.currencyCode}"
              loaderMargins="mt-2"
              ?shippable=${this.shippable}>
            </mhecomm-google-pay-button>
          ` : nothing}
      </div>
      <div>${this.isLoading ? html`${loading({m: 'mt-2'})}` : nothing}</div>`;
  }

  /**
   *
   * @param e collect Google Pay billing address, shipping address and payment info from event details.
   * Validate and save payment info, billing and shipping address.
   */
  async onCollectGooglePayInfo(e: Event) {
    const event = e as CustomEvent;
    const googlePayPaymentDetails: PaymentDetails = event.detail.paymentDetails;

    store.dispatch(updatePaymentDetails(googlePayPaymentDetails));

    await this.onCollectPaymentInfo(event);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-cart-google-pay-payment-method': GooglePayCartPaymentMethod;
  }
}
